import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import linkstate from 'linkstate';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { isEnabled as customerCreditEnabled, creditButton, isPartial as customerCreditIsPartial } from '../../lib/customerCredit';
import { conveneErrorMessage } from '../../lib/conveneErrorMessage';

import FormFields from '../../components/form/fields';
import Input from '../../components/form/input';
import PaymentVippsGeneric from '../../components/payment/vippsGeneric';
import SalePaymentVippsQr from '../../components/dyrejournal/salePaymentVippsQr';
import DyrejournalSaleAccountingTransfer from '../../components/dyrejournal/saleAccountingTransfer';
import ModuleHint from '../../components/dyrejournal/moduleHint';
import { appAlert, appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@withText(props => ({
    orderNumber: <Text id='input.orderNumber-help'>orderNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
    installation: <Text id='input.installation-help'>installation</Text>,
    url: <Text id='input.url-help'>url</Text>,
}))
@observer
class SalePayment extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            payDate: util.isoDate(new Date(), true),
            currentNetsTerminalId: util.get('currentNetsTerminalId'),
            vippsPaymentOpen: false,
            vippsPaymentAmount: 0,
            vippsPaymentMode: 'qr',
            conveneError: '',
            customerCreditError: '',
        };
    }

    changePayDate = async e => {
        const { value: payDate } = e.target;
        this.setState({ payDate });
    }

    startVippsPayment = async (e) => {
        const { partialAmount } = this.state;
        const { saleStore } = this.props.stores;
        const { newSale, currentPayments = [] } = saleStore;
        const restPayment = newSale.paymentMethod === 'creditNote' ? 0 : saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false), false, false);

        // Determine payment amount
        const amount = Math.abs(partialAmount) > 0 ? partialAmount : restPayment;

        this.setState({
            vippsPaymentOpen: true,
            vippsPaymentAmount: amount,
            vippsPaymentMode: 'qr',
        });
    }

    setVippsPaymentMode = (e) => {
        const { mode } = e.currentTarget.dataset;
        this.setState({ vippsPaymentMode: mode === 'link' ? 'link' : 'qr' });
    }

    // Done-callback for the in-clinic QR flow. The status poll only reports state and amount,
    // so the salePayment is linked to the payment through paymentUuidv4.
    vippsQrPaymentDoneCallback = async ({ amount, paymentUuidv4, paymentStatus }) => {
        if (paymentStatus !== 2 && paymentStatus !== 3) {
            return;
        }
        const { payDate } = this.state;
        const { callback = () => {} } = this.props;
        const { saleStore, salePaymentStore, userStore } = this.props.stores;
        const { newSale } = saleStore;
        const { user } = userStore;
        const currentDepartment = user.currentCustomerDepartment;

        await salePaymentStore.insert({
            sale: newSale.id,
            amount,
            vat: 0,
            paymentMethod: 'vipps',
            paidDate: payDate,
            customerDepartment: currentDepartment,
            paymentUuidv4,
        });

        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'vipps');
        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('paid'));
        saleStore.updateObjectKeyValue('newSale', 'paidDate', payDate);

        await saleStore.saveSale({
            calledFrom: 'salePayment.vippsQrPaymentDoneCallback',
            fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'],
        });

        this.setState({ vippsPaymentOpen: false });
        callback(true);
    }

    vippsPaymentCallback = async (data) => {
        console.log('[SalePayment] Vipps payment initiated:', data);
    }

    vippsPaymentDoneCallback = async ({ currency, amount, paymentUuidv4, paymentStatus, currentPayment }) => {
        console.log('[SalePayment] Vipps payment completed:', { currency, amount, paymentUuidv4, paymentStatus });

        const { payDate } = this.state;
        const { callback = () => {} } = this.props;
        const { saleStore, salePaymentStore, userStore } = this.props.stores;
        const { newSale, currentPayments = [] } = saleStore;
        const { user } = userStore;
        const currentDepartment = user.currentCustomerDepartment;

        if (paymentStatus === 2 || paymentStatus === 3) {
            // Save salePayment
            await salePaymentStore.insert({
                sale: newSale.id,
                amount: amount,
                vat: 0,
                paymentMethod: 'vipps',
                paidDate: payDate,
                customerDepartment: currentDepartment,
                payment: currentPayment.id,
                paymentUuidv4: paymentUuidv4,
            });

            // Mark sale as paid
            saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'vipps');
            saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('paid'));
            saleStore.updateObjectKeyValue('newSale', 'paidDate', payDate);

            await saleStore.saveSale({
                calledFrom: 'salePayment.vippsPaymentDoneCallback',
                fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate']
            });

            // Close Vipps payment modal
            this.setState({ vippsPaymentOpen: false });

            // Call parent callback
            callback(true);
        }
    }

    closeVippsPayment = () => {
        this.setState({ vippsPaymentOpen: false });
    }

    payViaConveneClick = async (e) => {
        e.preventDefault();
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        this.setState({ paymentInProgress: true, conveneError: '' });
        const response = await util.fetchApi(
            `/api/integrations/convene/orders/${newSale.id}`,
            { method: 'POST' },
            {},
        );
        this.setState({ paymentInProgress: false });
        if (response && response.status >= 400) {
            // `response.data` is the SALE on an error (post-orders.js), so reading
            // `data.title` here showed the owner and animal name instead of the
            // cause. The Convene message lives in `response.error`. See T-01609.
            this.setState({
                conveneError: conveneErrorMessage(response) || 'Failed to send order to Convene',
            });
            return;
        }
        const updated = response && response.data;
        if (updated && updated.id) {
            saleStore.updateObjectKeyValue('newSale', 'conveneOrderId', updated.conveneOrderId);
            saleStore.updateObjectKeyValue('newSale', 'conveneClaimId', updated.conveneClaimId);
            saleStore.updateObjectKeyValue('newSale', 'conveneInvoiceNumber', updated.conveneInvoiceNumber);
            saleStore.updateObjectKeyValue('newSale', 'conveneIsOpen', updated.conveneIsOpen);
            saleStore.updateObjectKeyValue('newSale', 'conveneAmounts', updated.conveneAmounts);
            saleStore.updateObjectKeyValue('newSale', 'status', updated.status);
        }
    }

    savePayment = async (e) => {
        let { partialAmount } = this.state;
        const { payDate = new Date() } = this.state;
        this.setState({ customerCreditError: '' });
        this.setState({ paymentInProgress: true });
        const { method, netsTerminal } = e.target.closest('button').dataset;
        const { callback = () => {}, drawerLevel = 1 } = this.props;
        const { appState, saleStore, salePaymentStore, userStore } = this.props.stores;
        const {
            saleTotal = 0,
            saleTax = 0,
            newSale = {},
            currentPayments = [],
        } = saleStore;
        const { user, customer } = userStore;
        const currentDepartment = user.currentCustomerDepartment;
        const restPayment = newSale.paymentMethod === 'creditNote' ? 0 : saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false), false, false);

        // Kundekonto: a spend goes partial when it does not cover the rest; both a spend
        // and a return go partial when earlier payments exist (the full path posts the
        // sale total and would over-credit or refuse); otherwise the full path.
        if (method === 'customerCredit') {
            const credit = this.getCreditButton();
            if (!credit || !(Math.abs(credit.amount) > 0)) {
                this.setState({ paymentInProgress: false });
                return false;
            }
            const hasEarlierPayments = currentPayments.length > 0;
            const spendIsPartial = credit.mode === 'use' && customerCreditIsPartial(credit.amount, restPayment);
            if (hasEarlierPayments || spendIsPartial) {
                partialAmount = credit.amount;
            } else {
                partialAmount = 0;
            }
        }

        // Forbruk (internal consumption): no payment record, no money changed hands.
        // Mark as paid with status 1006 so it is excluded from all financial reports.
        if (method === 'forbruk') {
            saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'forbruk');
            saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('forbruk'));
            saleStore.updateObjectKeyValue('newSale', 'paidDate', payDate);
            await saleStore.saveSale({
                calledFrom: 'salePayment.savePayment:forbruk',
                event: e,
                fieldsToSave: ['paymentMethod', 'status', 'paidDate'],
            });
            appState.trackSale('payment', newSale, { paymentMethod: 'forbruk', amount: 0 });
            this.setState({ paymentInProgress: false });
            callback(true);
            return;
        }

        if (Math.abs(partialAmount) > 0) {
            // saveField(id, field, value, updateMemory, opt = {}) {
            // await saleStore.saveField(newSale.id, 'paymentAmount', 'vipps', true);
            // await saleStore.saveField(newSale.id, 'paymentAmount', 'vipps', true);
            // await saleStore.save({
            //     id: newSale.id,
            //     paymentAmount: partialAmount,
            //     paymentMethod: method,
            // });

            if (netsTerminal && method === 'card') {
                const { drawerHeightSmall } = appState;
                appState.openDrawer('netsPayment', {
                    height: drawerHeightSmall,
                    netsTerminal,
                }, drawerLevel + 1);

                const result = await saleStore.netsCreateTransaction({
                    saleId: newSale.id,
                    amount: partialAmount,
                    netsTerminalId: netsTerminal,
                    payDate,
                });
                await saleStore.saveField(newSale.id, 'netsLastWebResponse', result, true);
                if (!result) {
                    const dialogAction = beginDialogAction(this, 'paymentFailure', () => [saleStore.newSale?.id]);
                    if (!dialogAction) return false;
                    try {
                        await appAlert('Nets transaction failed. Please try again.');
                        if (!dialogAction.isCurrent()) return false;
                        appState.toggleDrawer(false, drawerLevel + 1);
                    } finally {
                        dialogAction.finish();
                        this.setState({ paymentInProgress: false });
                    }
                    return false;
                }
                appState.toggleDrawer(false, drawerLevel + 1);
            }

            // Save salePayment
            const partialInsert = await salePaymentStore.insert({
                sale: newSale.id,
                // amount: method === 'creditNote' ? 0 - partialAmount : partialAmount,
                amount: partialAmount,
                vat: 0,
                paymentMethod: method,
                paidDate: payDate,
                customerDepartment: currentDepartment,
            });
            // Any refused payment (kundekonto, MVA-lås 423, ...) must not mark the sale as paid locally.
            if (partialInsert && partialInsert.status >= 400) {
                this.setState({ paymentInProgress: false, customerCreditError: partialInsert.message || 'Betalingen ble ikke registrert.' });
                return false;
            }

            const partialPayments = newSale.partialPayments || [];
            saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'partial');
            saleStore.updateObjectKeyValue('newSale', 'paymentAmount', parseInt(partialAmount, 10));
            saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('partial'));
            saleStore.updateObjectKeyValue('newSale', 'partialPayments', [...partialPayments, {
                amount: parseInt(partialAmount, 10),
                vat: 0,
                date: payDate,
                paymentMethod: method,
            }]);
        } else {
            let totalAmount = saleStore.sumTotal(newSale.products, false);
            // Cash-only øreavrunding: only cash rounds to a whole krone; other methods charge exact øre.
            if (saleStore.oreRoundingCashOnly && method === 'cash') {
                totalAmount = Math.round(totalAmount);
            }
            if (method === 'roundoff') {
                totalAmount = restPayment;
            }

            // Allow a sub-krone overage when rounding cash up (e.g. 1235 vs exact 1234.50).
            const overpayTolerance = (saleStore.oreRoundingCashOnly && method === 'cash') ? 0.5 : 0;
            if (totalAmount > restPayment + overpayTolerance) {
                (await appAlert('Beløpet er høyere enn gjenstående beløp. Bruk delbetaling for å legge inn betaling.'));
                this.setState({ paymentInProgress: false });
                return false;
            }

            if (netsTerminal && method === 'card') {
                const { drawerHeightSmall } = appState;
                appState.openDrawer('netsPayment', {
                    height: drawerHeightSmall,
                    netsTerminal,
                }, drawerLevel + 1);

                const result = await saleStore.netsCreateTransaction({
                    saleId: newSale.id,
                    amount: totalAmount,
                    netsTerminalId: netsTerminal,
                    payDate,
                });
                if (!result) {
                    const dialogAction = beginDialogAction(this, 'paymentFailure', () => [saleStore.newSale?.id]);
                    if (!dialogAction) return false;
                    try {
                        await appAlert('Nets transaction failed. Please try again.');
                        if (!dialogAction.isCurrent()) return false;
                        appState.toggleDrawer(false, drawerLevel + 1);
                    } finally {
                        dialogAction.finish();
                        this.setState({ paymentInProgress: false });
                    }
                    return false;
                }
                appState.toggleDrawer(false, drawerLevel + 1);
            }

            // Save salePayment
            const fullInsert = await salePaymentStore.insert({
                sale: newSale.id,
                amount: method === 'creditNote' ? 0 - totalAmount : totalAmount,
                vat: 0,
                paymentMethod: method,
                paidDate: payDate,
                customerDepartment: currentDepartment,
            });
            // Any refused payment (kundekonto, MVA-lås 423, ...) must not mark the sale as paid locally.
            if (fullInsert && fullInsert.status >= 400) {
                this.setState({ paymentInProgress: false, customerCreditError: fullInsert.message || 'Betalingen ble ikke registrert.' });
                return false;
            }

            if (newSale.paymentMethod !== 'invoice' && method !== 'roundoff') {
                saleStore.updateObjectKeyValue('newSale', 'paymentMethod', method);
            }
            if (method === 'cash' || method === 'card' || method === 'cardExternal' || method === 'creditCard' || method === 'vipps' || method === 'account' || method === 'roundoff' || method === 'customerCredit') {
                // TODO: Add partial payment support
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('paid'));
                saleStore.updateObjectKeyValue('newSale', 'paidDate', payDate);
            } else if (method === 'invoice') {
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('invoice'));
            } else if (method === 'account') {
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('account'));
            } else if (method === 'creditNote') {
                saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('creditNote'));
            }
        }

        if (newSale.paymentMethod === 'invoice') {
            await saleStore.saveSale({ calledFrom: 'salePayment.savePayment', event: e, fieldsToSave: ['paymentAmount', 'status', 'paidDate'] });
        } else {
            await saleStore.saveSale({ calledFrom: 'salePayment.savePayment', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        }

        appState.trackSale('payment', newSale, { paymentMethod: newSale.paymentMethod, amount: newSale.paymentAmount });

        if (method === 'customerCredit') {
            await this.loadCustomerCredit();
        }

        this.setState({ paymentInProgress: false });
        callback(true);
    }

    saveInvoice = async (e) => {
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'invoice');
        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('invoice'));

        await saleStore.saveSale({ calledFrom: 'salePayment.saveInvoice', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        this.setState({ paymentInProgress: false });
        callback(true);
    }

    saveAsPaid = async e => {
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale, currentPayments = [] } = saleStore;

        // Get last payment and mark as paid with this paymentMethod
        // Sort by paidDate descending
        const lastPayment = [...currentPayments].sort((a, b) => new Date(b.paidDate) - new Date(a.paidDate))[0];
        if (lastPayment && lastPayment.paymentMethod) {
            saleStore.updateObjectKeyValue('newSale', 'paymentMethod', lastPayment.paymentMethod);
        } else {
            saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'cash');
        }
        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('paid'));
        // Use the last payment's date if available, then sale date, then current date
        const paidDate = (lastPayment && lastPayment.paidDate) ? new Date(lastPayment.paidDate)
            : newSale.saleDate ? new Date(newSale.saleDate)
            : new Date();
        saleStore.updateObjectKeyValue('newSale', 'paidDate', paidDate);

        await saleStore.saveSale({ calledFrom: 'salePayment.saveAsPaid', event: e, fieldsToSave: ['status', 'paymentMethod', 'paidDate'] });
        this.setState({ paymentInProgress: false });
        callback(true);
    }

    createCreditNote = async (e) => {
        // 1. Mark current sale as invoiceCredited
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'invoiceCredited');
        saleStore.updateObjectKeyValue('newSale', 'status', saleStore.getSaleStatus('invoiceCredited'));

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNote', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        this.setState({ paymentInProgress: false });

        // 2. Create credit note sale with negated prices
        // Prepare data BEFORE calling createNewSale to avoid race conditions
        const creditNoteProducts = (newSale.products || []).map(prod => {
            const p = { ...prod };
            p.discountAmount = 0 - (p.discountAmount || 0);
            p.price = 0 - (p.price || 0);
            // Negate segment prices for credit note
            if (p.medicine) p.medicine = 0 - p.medicine;
            if (p.fee) p.fee = 0 - p.fee;
            if (p.equipment) p.equipment = 0 - p.equipment;
            if (p.other) p.other = 0 - p.other;
            return p;
        });

        // Pass all data and skip flags directly to createNewSale to prevent
        // excessive re-renders that were causing the browser to hang
        saleStore.createNewSale({
            user: newSale.user,
            visitors: newSale.visitors || [],
            animals: newSale.animals || [],
            products: creditNoteProducts,
            paymentMethod: 'creditNote',
            status: saleStore.getSaleStatus('creditNote'),
            // Skip clearing auxiliary observables to prevent excessive re-renders
            skipSearchResults: true,
            skipVeterinaries: true,
            skipVisitorResults: true,
            skipAnimalResults: true,
            skipAnimalJournals: true,
            skipVisitorAnimals: true,
            skipVisitorInvoices: true,
            skipProductResults: true,
            skipVisitorSales: true,
            skipCurrentVisitorSales: true,
            skipCurrentPayments: true,
        });

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNote', event: e });
        callback(true);
    }

    createCreditNoteSale = async (e) => {
        // 1. Mark current sale as credited
        this.setState({ paymentInProgress: true });
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', 'saleCredited');

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNoteSale', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
        this.setState({ paymentInProgress: false });

        // 2. Create credit note sale with negated prices
        // Prepare data BEFORE calling createNewSale to avoid race conditions
        const creditNoteProducts = (newSale.products || []).map(prod => {
            const p = { ...prod };
            p.discountAmount = 0 - (p.discountAmount || 0);
            p.price = 0 - (p.price || 0);
            // Negate segment prices for credit note
            if (p.medicine) p.medicine = 0 - p.medicine;
            if (p.fee) p.fee = 0 - p.fee;
            if (p.equipment) p.equipment = 0 - p.equipment;
            if (p.other) p.other = 0 - p.other;
            return p;
        });

        // Pass all data and skip flags directly to createNewSale to prevent
        // excessive re-renders that were causing the browser to hang
        saleStore.createNewSale({
            user: newSale.user,
            visitors: newSale.visitors || [],
            animals: newSale.animals || [],
            products: creditNoteProducts,
            paymentMethod: 'creditNote',
            status: 0,
            // Skip clearing auxiliary observables to prevent excessive re-renders
            skipSearchResults: true,
            skipVeterinaries: true,
            skipVisitorResults: true,
            skipAnimalResults: true,
            skipAnimalJournals: true,
            skipVisitorAnimals: true,
            skipVisitorInvoices: true,
            skipProductResults: true,
            skipVisitorSales: true,
            skipCurrentVisitorSales: true,
            skipCurrentPayments: true,
        });

        await saleStore.saveSale({ calledFrom: 'salePayment.createCreditNoteSale', event: e });
        callback(true);
    }

    unlockPaymentMethod = async (e) => {
        const { saleStore } = this.props.stores;
        saleStore.updateObjectKeyValue('newSale', 'paymentMethod', null);
        saleStore.updateObjectKeyValue('newSale', 'paidDate', null);
        saleStore.updateObjectKeyValue('newSale', 'paymentAmount', 0);
        saleStore.updateObjectKeyValue('newSale', 'status', 0);
        await saleStore.saveSale({ calledFrom: 'salePayment.unlockPaymentMethod', event: e, fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
    }

    togglePartialPayment = e => {
        this.setState({ partialPayment: !this.state.partialPayment });
    }

    toggleCashPayment = e => {
        this.setState({ cashPayment: !this.state.cashPayment });
    }

    clearPaidAmount = e => {
        this.setState({ paidAmount: 0 });
    }

    loadSalePayments = async saleId => {
        if (!util.isNumber(saleId)) {
            return false;
        }
        const { salePaymentStore, saleStore } = this.props.stores;
        const response = await salePaymentStore.load({
            query: {
                sale: saleId,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('currentPayments', response);
    }

    loadCustomerCredit = async () => {
        const { saleStore, userStore, visitorCreditStore } = this.props.stores;
        const { newSale = {} } = saleStore;
        const { customer } = userStore;
        const visitorId = newSale.visitors && newSale.visitors[0] ? newSale.visitors[0].id : 0;
        if (!customerCreditEnabled(customer) || !(visitorId > 0)) {
            return 0;
        }
        return visitorCreditStore.loadBalance(visitorId);
    }

    getCreditButton = () => {
        const { saleStore, userStore, visitorCreditStore } = this.props.stores;
        const { newSale = {}, currentPayments = [] } = saleStore;
        const { customer } = userStore;
        const visitorId = newSale.visitors && newSale.visitors[0] ? newSale.visitors[0].id : 0;
        const total = saleStore.sumTotal(newSale.products, false);
        const rest = newSale.paymentMethod === 'creditNote' ? 0 : saleStore.sumRestPayments(currentPayments, total, false, false);
        const { balances = {} } = visitorCreditStore;
        const balance = balances[visitorId] || 0;
        return creditButton({
            enabled: customerCreditEnabled(customer),
            hasVisitor: visitorId > 0,
            total,
            rest,
            balance,
        });
    }

    onClickDeleteSalePayment = async e => {
        e.preventDefault();
        e.stopPropagation();
        const dialogAction = beginDialogAction(this, 'onClickDeleteSalePayment');
        if (!dialogAction) return false;
        try {
            const dialogTargetData = { ...e.target.closest('tr').dataset };



            if (!((await appConfirm('Are you sure you want to delete this salePayment?')) && dialogAction.isCurrent())) {
                return;
            }

            const { id } = dialogTargetData;
            const { salePaymentStore, saleStore } = this.props.stores;
            // console.log('onClickDeleteSalePayment', id, parseInt(id, 10));
            const { newSale } = saleStore;
            const deleteResponse = await salePaymentStore.deletePayment(parseInt(id, 10));
            if (deleteResponse?.status >= 400) {
                this.setState({ customerCreditError: deleteResponse.message || 'Betalingen ble ikke slettet.' });
            }

            setTimeout(() => {
                if (newSale?.id) {
                    this.loadSalePayments(newSale.id);
                }
            }, 1000);
        } finally {
            dialogAction.finish();
        }
    }

    chooseNetsTerminal = e => {
        const { value: currentNetsTerminalId } = e.target;
        this.setState({ currentNetsTerminalId });
        util.set('currentNetsTerminalId', currentNetsTerminalId);
    }

    componentDidMount() {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        this.loadSalePayments(newSale.id);
        this.loadCustomerCredit();

        const { userStore } = this.props.stores;
        const { customer } = userStore;
        const { netsTerminals = [] } = customer;
        let currentNetsTerminalId = null;
        if (netsTerminals.length > 0) {
            currentNetsTerminalId = netsTerminals[0].terminalId;
            this.setState({
                currentNetsTerminalId,
            });
        }
    }

    render() {
        const { week, isNew, drawerLevel } = this.props;
        const { sections, partialPayment, paidAmount, payDate, paymentInProgress, cashPayment, currentNetsTerminalId, vippsPaymentOpen, vippsPaymentAmount, vippsPaymentMode } = this.state;
        const { userStore, saleStore } = this.props.stores;
        const { customer, user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const {
            saleTotal = 0,
            saleTax = 0,
            newSale = {},
            currentPayments = [],
        } = saleStore;

        const saleIsLocked = !!newSale.paymentMethod;
        // const saleIsLocked = diff?.days > 360 || newSale.status > 0;
        const restPayment = newSale.paymentMethod === 'creditNote' ? 0 : saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false), false, false);

        const { featureFlags = {}, netsTerminals = [] } = customer;
        const currentNetsTerminal = netsTerminals.find(e => e.terminalId === currentNetsTerminalId) || {};
        const { customerCreditError } = this.state;
        const credit = this.getCreditButton();

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        {newSale.paymentMethod === 'invoice' ? <>
                            <Text id='dyrejournal.invoice-payment'>Invoice payment</Text>
                        </> : <>
                            <Text id='dyrejournal.sale-payment'>Sale Payment</Text>
                        </>}
                    </div>
                    <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                        {newSale.paymentMethod === 'invoice' && <div class='px-3'>
                            <ModuleHint module='accounting' stores={this.props.stores} />
                            <ModuleHint module='convenePayment' stores={this.props.stores} />
                        </div>}
                        <div class='d-flex flex-column mt-0 pt-2 pe-5' style='font-size: 1.5em;'>
                            <div class='d-flex flex-row justify-content-end'>
                                <div><Text id='dyrejournal.payment-total'>Total</Text></div>
                                <div class='ms-3 text-success font-weight-bold'>{util.format(saleTotal, 2, ',', ' ')}</div>
                            </div>
                            <div class='d-flex flex-row justify-content-end font-weight-lighter'>
                                <div><Text id='dyrejournal.payment-tax'>Tax</Text></div>
                                <div class='ms-3'>{saleTax}</div>
                            </div>
                            <div class='d-flex flex-row justify-content-end font-weight-bold text-danger'>
                                <div><Text id='dyrejournal.payment-rest'>Rest payment</Text></div>
                                <div class='ms-3'>{util.format(restPayment, 2)}</div>
                            </div>
                        </div>

                        {newSale.conveneOrderId > 0 && (
                            <div class='card border-info mx-3 mb-3'>
                                <div class='card-body'>
                                    <h6 class='card-title'>
                                        <i class='fa-duotone fa-paper-plane me-2' />
                                        <Text id='convene.sentStatus'>Sent to Convene</Text>
                                    </h6>
                                    <dl class='row mb-0 small'>
                                        <dt class='col-5'><Text id='convene.orderId'>Order ID</Text></dt>
                                        <dd class='col-7'>{newSale.conveneOrderId}</dd>
                                        {newSale.conveneClaimId > 0 && (
                                            <>
                                                <dt class='col-5'><Text id='convene.claimId'>Claim ID</Text></dt>
                                                <dd class='col-7'>{newSale.conveneClaimId}</dd>
                                            </>
                                        )}
                                        {newSale.conveneInvoiceNumber > 0 && (
                                            <>
                                                <dt class='col-5'><Text id='convene.invoiceNumber'>Invoice #</Text></dt>
                                                <dd class='col-7'>{newSale.conveneInvoiceNumber}</dd>
                                            </>
                                        )}
                                        {newSale.conveneAmounts && newSale.conveneAmounts.balance != null && (
                                            <>
                                                <dt class='col-5'><Text id='convene.balance'>Balance</Text></dt>
                                                <dd class='col-7'>{newSale.conveneAmounts.balance} NOK</dd>
                                            </>
                                        )}
                                    </dl>
                                </div>
                            </div>
                        )}

                        {!newSale.id ? <>
                            <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                <div class='spinner-grow text-secondary' role='status'>
                                    <span class='sr-only'>Loading...</span>
                                </div>
                            </div>
                        </> : <>
                            {Math.abs(restPayment) > 0 ? <>
                                <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                    <div class='input-group w-50'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <Text id='dyrejournal.payment-date'>Payment date</Text>
                                        </div>
                                        <input
                                            class='form-control inputForm rounded-pill-right'
                                            type='datetime-local'
                                            onInput={this.changePayDate}
                                            value={payDate}
                                        />
                                    </div>
                                </div>

                                {partialPayment ? <>
                                    <div class='w-100 d-flex flex-column justify-content-center align-items-center mt-3'>
                                    <div class='alert alert-primary rounded-lg' role='alert'>
                                        <h5><Text id='dyrejournal.partial-payment'>Partial Payment</Text></h5>
                                        <p><Text id='dyrejournal.partial-payment-help'>Enter payment amount and choose partial payment method below.</Text></p>
                                    </div>
                                        <div class='input-group w-75'>
                                            <div class='input-group-text rounded-pill-left'>
                                                <Text id='dyrejournal.partial-payment'>Partial Payment</Text>
                                            </div>
                                            <input
                                                type='text'
                                                class='form-control form-control-lg inputForm'
                                                placeholder='Beløp'
                                                min={0}
                                                max={saleTotal}
                                                onInput={linkstate(this, 'partialAmount')}
                                            />
                                            <button type='button' class='btn btn-danger rounded-none' onClick={this.togglePartialPayment}>
                                                <i class='fa-solid fa-times' />
                                            </button>
                                            <div class='input-group-text rounded-pill-right'><Text id='dyrejournal.currency-nok'>NOK</Text></div>
                                            <small class='form-text text-muted px-2 pt-1'><Text id='dyrejournal.partial-payment-rest'>Rest amount</Text>: {util.format(restPayment, 2)}</small>
                                        </div>
                                        <small class='form-text text-muted mt-2'><Text id='dyrejournal.partial-payment-mark'>Choose partial payment method below.</Text></small>
                                    </div>
                                </> : <>
                                    {cashPayment ? <>
                                        <div class='w-100 d-flex flex-column justify-content-center align-items-center mt-3'>
                                            <div class='input-group w-50'>
                                                <div class='input-group-text rounded-pill-left'>
                                                    <Text id='dyrejournal.cash-amount'>Cash amount</Text>
                                                </div>
                                                <input
                                                    type='text'
                                                    class={`form-control inputForm ${paidAmount ? '' : 'rounded-pill-right'}`}
                                                    min={0}
                                                    onInput={linkstate(this, 'paidAmount')}
                                                    value={paidAmount}
                                                />
                                                {paidAmount > 0 && <>
                                                    <button type='button' class='btn btn-danger rounded-pill-right' onClick={this.clearPaidAmount}>
                                                        <i class='fa-solid fa-times' />
                                                    </button>
                                                </>}
                                            </div>
                                        </div>
                                        {paidAmount > 0 && <div class='w-100 d-flex flex-column justify-content-center align-items-center mt-3'>
                                            <div class='input-group w-50'>
                                                <div class='input-group-text rounded-pill-left'><Text id='dyrejournal.money-bak'>Money back</Text></div>
                                                <input
                                                    disabled={true}
                                                    type='text'
                                                    class='form-control inputForm rounded-pill-right'
                                                    value={paidAmount - saleTotal}
                                                />
                                            </div>
                                        </div>}
                                    </> : <>
                                        <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                            <button type='button' class='btn btn-link' onClick={this.toggleCashPayment}>
                                                <i class='fa-duotone fa-money-bill' /> <Text id='dyrejournal.sale-pay-with-cash'>Show Cash Calculator</Text>
                                            </button>
                                        </div>
                                    </>}
                                </>}

                                <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                    {!featureFlags.paymentHideCash && <>
                                        <button type='button' class='btn btn-lg btn-success rounded-pill' disabled={paymentInProgress} onClick={this.savePayment} data-method='cash'>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('cash')} /> <Text id='dyrejournal.sale-payment-cash'>Cash</Text>
                                        </button>
                                    </>}

                                    {!!featureFlags.nets && <>
                                        <button
                                            type='button'
                                            class='btn btn-outline-primary rounded-pill ms-3 position-relative'
                                            style={`${currentNetsTerminal ? 'width: 150px;' : ''}`}
                                            disabled={paymentInProgress}
                                            onClick={this.savePayment}
                                            data-method='card'
                                            data-nets-terminal={currentNetsTerminalId}
                                        >
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('card')} /> <Text id='dyrejournal.sale-payment-card'>Card</Text>
                                            {(netsTerminals.length > 0 && currentNetsTerminal) ? <>
                                                <div class='position-absolute' style='bottom: 0px; left: 50%; transform: translateX(-50%); font-size: 0.6em;'>
                                                    <nobr>{currentNetsTerminal.name}</nobr>
                                                </div>
                                            </> : <></>}
                                        </button>
                                    </>}

                                    {!featureFlags.paymentHideCard && <>
                                        <button
                                            type='button'
                                            class='btn btn-primary rounded-pill ms-3 position-relative'
                                            disabled={paymentInProgress}
                                            onClick={this.savePayment}
                                            data-method='card'
                                        >
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('card')} /> <Text id='dyrejournal.sale-payment-card'>Card</Text>
                                        </button>
                                    </>}

                                    {!!featureFlags.cardExternalTerminal && <>
                                        <button
                                            type='button'
                                            class='btn btn-primary rounded-pill ms-3 position-relative'
                                            disabled={paymentInProgress}
                                            onClick={this.savePayment}
                                            data-method='cardExternal'
                                        >
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('cardExternal')} /> <Text id='dyrejournal.sale-payment-cardExternal'>Card (external terminal)</Text>
                                        </button>
                                    </>}

                                    {featureFlags.creditCard > 0 && <>
                                        <button
                                            type='button'
                                            class='btn btn-info rounded-pill ms-3'
                                            disabled={paymentInProgress}
                                            onClick={this.savePayment}
                                            data-method='creditCard'
                                        >
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>}
                                            <i class={saleStore.getPaymentMethodIcon('creditCard')} /> Kredittkort
                                        </button>
                                    </>}

                                    {!featureFlags.paymentHideVipps && <>
                                        {/* Integrated in-clinic Vipps (QR the customer scans) has its own flag, switched on by
                                            the clinic on the Vipps card under Integrations: featureFlags.vippsIntegration is the
                                            Vipps module used by the webshop/online flows and does not imply in-person payment. */}
                                        {featureFlags.vippsInClinicPayment ? <>
                                            <button type='button' class='btn btn-orange rounded-pill ms-3' disabled={paymentInProgress} onClick={this.startVippsPayment}>
                                                {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('vipps')} /> <Text id='dyrejournal.sale-payment-vipps'>Vipps</Text>
                                            </button>
                                        </> : <>
                                            <button type='button' class='btn btn-orange rounded-pill ms-3' disabled={paymentInProgress} onClick={this.savePayment} data-method='vipps'>
                                                {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('vipps')} /> <Text id='dyrejournal.sale-payment-vipps'>Vipps</Text>
                                            </button>
                                        </>}
                                    </>}
                                    {!featureFlags.paymentHideAccount && <>
                                        <button type='button' class='btn btn-danger rounded-pill ms-3' disabled={paymentInProgress} onClick={this.savePayment} data-method='account'>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('account')} /> <Text id='dyrejournal.sale-payment-account'>Account</Text>
                                        </button>
                                    </>}

                                    {credit && <>
                                        <button
                                            type='button'
                                            class='btn btn-success rounded-pill ms-3'
                                            disabled={paymentInProgress}
                                            onClick={this.savePayment}
                                            data-method='customerCredit'
                                        >
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>}
                                            <i class={saleStore.getPaymentMethodIcon('customerCredit')} />{' '}
                                            {credit.mode === 'return' ? <>
                                                <Text id='dyrejournal.sale-payment-customerCredit-return'>Put on customer account</Text>
                                            </> : <>
                                                <Text id='dyrejournal.sale-payment-customerCredit-use' fields={{ amount: util.format(credit.amount, 2, ',', ' ') }}>
                                                    Customer account (NOK {util.format(credit.amount, 2, ',', ' ')} available)
                                                </Text>
                                            </>}
                                        </button>
                                    </>}

                                    {!!featureFlags.convenePayment
                                        && customer && customer.conveneConfigured
                                        && !newSale.paymentMethod
                                        && !(newSale.conveneOrderId > 0)
                                        && (
                                            <button
                                                type='button'
                                                class='btn btn-outline-primary rounded-pill ms-3'
                                                disabled={paymentInProgress}
                                                onClick={this.payViaConveneClick}
                                            >
                                                {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>}
                                                <i class='fa-duotone fa-file-invoice me-2' />
                                                <Text id='dyrejournal.payViaConvene'>Pay via Convene</Text>
                                            </button>
                                        )
                                    }

                                    {partialPayment ? <>
                                        <button type='button' class='btn btn-outline-secondary rounded-pill ms-3' disabled={paymentInProgress} onClick={this.savePayment} data-method='creditNote'>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('creditNote')} /> <Text id='dyrejournal.sale-payment-creditNote'>creditNote</Text>
                                        </button>
                                    </> : <></>}
                                </div>

                                {this.state.conveneError !== '' && (
                                    <div class='w-100 d-flex flex-row justify-content-center mt-2'>
                                        <div class='alert alert-danger'>
                                            <strong><Text id='convene.error'>Convene error</Text>:</strong> {this.state.conveneError}
                                        </div>
                                    </div>
                                )}

                                {/* Persisted on the sale by post-orders.js; survives closing the drawer.
                                    Hidden once an order id exists so a stale error never shows on a sent sale. */}
                                {!!newSale.conveneLastError?.message && !(newSale.conveneOrderId > 0) && (
                                    <div class='w-100 d-flex flex-row justify-content-center mt-2'>
                                        <div class='alert alert-warning py-2 px-3 mb-0'>
                                            <i class='fa-solid fa-triangle-exclamation me-1' />
                                            <strong><Text id='convene.lastError'>Last Convene error</Text></strong>
                                            {newSale.conveneLastError.at && <> ({util.formatDate(newSale.conveneLastError.at, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })})</>}
                                            : {newSale.conveneLastError.message}
                                        </div>
                                    </div>
                                )}

                                {customerCreditError !== '' && (
                                    <div class='w-100 d-flex flex-row justify-content-center mt-2'>
                                        <div class='alert alert-danger py-1 px-3 mb-0' role='alert'>{customerCreditError}</div>
                                    </div>
                                )}

                                {netsTerminals.length > 0 && <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                    <select
                                        class='form-control form-control-lg rounded-pill px-3'
                                        onChange={this.chooseNetsTerminal}
                                    >
                                        <option value=''><Text id='dyrejournal.sale-terminal-select'>Select terminal</Text></option>
                                        {netsTerminals.map(e => <option value={e.terminalId} selected={currentNetsTerminalId === e.terminalId}>{e.name} ({e.terminalId})</option>)}
                                    </select>
                                </div>}

                                <div class='w-100 d-flex flex-row justify-content-center mt-3 pb-3 border-bottom'>
                                    {partialPayment ? <>
                                    </> : <>
                                        <button type='button' class='btn btn-outline-secondary rounded-pill' onClick={this.togglePartialPayment}>
                                            <i class={saleStore.getPaymentMethodIcon('partial')} /> <Text id='dyrejournal.sale-payment-partial'>Partial</Text>
                                        </button>
                                        <button type='button' class='btn btn-secondary rounded-pill ms-3' disabled={paymentInProgress} onClick={this.savePayment} data-method='roundoff'>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('roundoff')} /> <Text id='dyrejournal.sale-payment-roundoff'>Roundoff</Text>
                                        </button>
                                        <button type='button' class='btn btn-outline-info rounded-pill ms-3' disabled={paymentInProgress} onClick={this.savePayment} data-method='forbruk'>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('forbruk')} /> <Text id='dyrejournal.sale-payment-forbruk'>Forbruk</Text>
                                        </button>
                                    </>}
                                </div>
                            </> : <>
                                <div class='w-100 d-flex flex-row justify-content-center mt-3 pb-3 border-bottom'>
                                    {saleIsLocked ? <>

                                        {newSale.paymentMethod === 'creditNote' ? <></> : <>
                                            <button type='button' class='btn btn-warning rounded-pill ms-3' disabled={paymentInProgress} onClick={this.createCreditNoteSale} data-method='creditNote'>
                                                {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('creditNote')} /> <Text id='dyrejournal.sale-create-creditNote'>Create Credit Note</Text>
                                            </button>
                                        </>}

                                    </> : <>
                                        <button type='button' class='btn btn-primary rounded-pill' disabled={paymentInProgress} onClick={this.saveAsPaid}>
                                            {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={`fa-solid fa-check`} /> <Text id='dyrejournal.sale-payment-mark-as-paid'>Mark as paid</Text>
                                        </button>
                                    </>}
                                </div>
                            </>}

                            {/* Transfer a paid sale (or credit note) to accounting from the
                                payment drawer — same per-provider rules as the sale list. */}
                            {saleIsLocked && <DyrejournalSaleAccountingTransfer stores={this.props.stores} />}

                            {saleIsLocked || newSale.status === 2 ? <>
                                <div class='w-100 d-flex flex-column align-items-center mt-5'>
                                    <div>
                                        <Text id='dyrejournal.sale-payment-method-already-choosen'>Payment method for sale already set.</Text>  (<i class={`${saleStore.getPaymentMethodIcon(newSale.paymentMethod)} mx-1`} /> {saleStore.getPaymentMethodText(newSale.paymentMethod)})
                                    </div>
                                    <button type='button' class='btn btn-link  rounded-pill ms-3' onClick={this.unlockPaymentMethod}>
                                        <i class='fa-solid fa-lock' /> <Text id='dyrejournal.sale-unlock-payment-method'>Unlock payment method</Text>
                                    </button>
                                </div>
                                {/* Add invoice payment methods.<br />
                                restPayment: {util.format(restPayment, 2)}<br />
                                newSale.paymentMethod: {newSale.paymentMethod}<br /> */}
                                {/* <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                    <button type='button' class='btn btn-info rounded-pill ms-3' disabled={paymentInProgress} onClick={this.createCreditNoteSale} data-method='creditNote'>
                                        {paymentInProgress && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={saleStore.getPaymentMethodIcon('creditNote')} /> <Text id='dyrejournal.sale-create-creditNote'>Create Credit Note</Text>
                                    </button>
                                </div> */}
                            </> : <>
                            </>}

                            {currentPayments && currentPayments.length > 0 && <>
                                <div class='font-weight-lighter px-3 box-header mt-5'>
                                    <Text id='dyrejournal.existing-sale-payments'>Existing Payments</Text>
                                </div>
                                <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                                    <div class='w-100 d-flex flex-column'>
                                        <div class={`d-flex flex-column mx-3 px-2 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                            <small>
                                            <div class={`table-responsive ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                                <table class='table table-striped table-sm mb-0'>
                                                    <thead>
                                                        <tr>
                                                            <th class='text-muted text-start'><nobr><Text id='dyrejournal.partial-payment-date'>Date</Text></nobr></th>
                                                            <th class='text-muted text-center'><nobr><Text id='dyrejournal.partial-payment-method'>Method</Text></nobr></th>
                                                            <th class='text-muted text-end'><nobr><Text id='dyrejournal.sale.amount'>Amount</Text></nobr></th>
                                                            <th>&nbsp;</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentPayments.map((e, idx) => <>
                                                            <tr data-id={e.id}>
                                                                <td>{util.formatDate(e.paidDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</td>
                                                                <td class='text-center'><i class={saleStore.getPaymentMethodIcon(e.paymentMethod)} /> {e.paymentMethod}</td>
                                                                <td class='text-end'>{util.format(e.amount, 2, ',', ' ')}</td>
                                                                <td class='d-none d-sm-table-cell' onClick={this.onClickDeleteSalePayment} data-id={e.id}>
                                                                    <i class='fa-solid fa-trash text-danger' />
                                                                </td>
                                                            </tr>
                                                        </>)}
                                                    </tbody>
                                                </table>
                                            </div>
                                            </small>
                                            {/* <xmp>{JSON.stringify(currentPayments, null, 4)}</xmp> */}
                                        </div>
                                    </div>
                                </div>
                            </>}

                        </>}

                    </div>
                </div>
            </div>

            {/* Vipps Payment Modal */}
            {vippsPaymentOpen && <>
                <div class='modal-backdrop show' onClick={this.closeVippsPayment} style='z-index: 1040;'></div>
                <div class='modal show d-block' style='z-index: 1050;' tabindex='-1'>
                    <div class='modal-dialog modal-lg modal-dialog-centered'>
                        <div class={`modal-content ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='modal-header border-bottom'>
                                <h5 class='modal-title'>
                                    <i class={saleStore.getPaymentMethodIcon('vipps')} /> <Text id='dyrejournal.vipps-payment-title'>Vipps Payment</Text>
                                </h5>
                                <button type='button' class='btn-close' onClick={this.closeVippsPayment}></button>
                            </div>
                            <div class='modal-body'>
                                <div class='d-flex justify-content-center mb-3'>
                                    <div class='btn-group' role='group'>
                                        <button
                                            type='button'
                                            class={`btn btn-sm ${vippsPaymentMode === 'qr' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            data-mode='qr'
                                            onClick={this.setVippsPaymentMode}
                                        >
                                            <i class='fa-duotone fa-qrcode me-1' /> <Text id='dyrejournal.vipps-mode-qr'>QR code</Text>
                                        </button>
                                        <button
                                            type='button'
                                            class={`btn btn-sm ${vippsPaymentMode === 'link' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            data-mode='link'
                                            onClick={this.setVippsPaymentMode}
                                        >
                                            <i class='fa-duotone fa-mobile-screen me-1' /> <Text id='dyrejournal.vipps-mode-link'>Open Vipps</Text>
                                        </button>
                                    </div>
                                </div>
                                {vippsPaymentMode === 'qr' ? <>
                                    <SalePaymentVippsQr
                                        key={`vipps-qr-${newSale.id || 'new'}-${vippsPaymentAmount}`}
                                        stores={this.props.stores}
                                        title={`Sale #${newSale.id || 'New'}`}
                                        amount={vippsPaymentAmount}
                                        customer={newSale.customer}
                                        sale={newSale.id}
                                        callbackPaymentDone={this.vippsQrPaymentDoneCallback}
                                    />
                                </> : <>
                                    <PaymentVippsGeneric
                                        stores={this.props.stores}
                                        title={`Sale #${newSale.id || 'New'}`}
                                        amount={vippsPaymentAmount}
                                        customer={newSale.customer}
                                        sale={newSale.id}
                                        callback={this.vippsPaymentCallback}
                                        callbackPaymentDone={this.vippsPaymentDoneCallback}
                                    />
                                </>}
                            </div>
                        </div>
                    </div>
                </div>
            </>}
        </>);
    }
}

export default SalePayment;
